<template>
  <b-card class="full-funnel-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body :title="'CRM Full Funnel Chart'" role="button" tabindex="0">
    <template #header>
      <h6 class="mb-0">CRM Full Funnel</h6>
    </template>
    <b-card-body>
      <!-- {{ safeSeries }} -->
      <apexchart v-if="chartReady && series && series.length" type="bar" height="350" :options="chartOptions" :series="series"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'FullFunnelChart',
  emits: ['chart-point-click'],
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      series: [],
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          stacked: false, // Changed to false as per processChartData
          stackType: undefined, // Changed to undefined as per processChartData
          toolbar: {
            show: false,
          },
          events: {
            dataPointSelection: this.handleDataPointSelection,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
          },
        },
        dataLabels: {
          enabled: true,
        },
        xaxis: {
          categories: [], // Stage names will go here
        },
        yaxis: {
          title: {
            text: 'Percentage of Leads',
          },
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: 'bottom',
          horizontalAlign: 'left',
          offsetX: 40,
        },
      },
    };
  },
  computed: {
    safeSeries() {
      // const s = (this.series || []).filter(Boolean);
      // const mapped = s.map((item) => {
      //   const raw = item && typeof item === 'object' ? item : {};
      //   return {
      //     name: raw.name || 'Leads',
      //     data: (Array.isArray(raw.data) ? raw.data : []).map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0)),
      //   };
      // });
      // return mapped.length ? mapped : [{ name: 'Leads', data: [] }];
      return this.series;
    },
    chartReady() {
      const chart = this.chartOptions && this.chartOptions.chart;
      return chart && typeof chart.height === 'number' && chart.height > 0;
    },
  },
  watch: {
    dashboardData: { deep: true, handler(v) { if (v && v.full_funnel_chart != null && hasRight(this.user, 'dashboard', 'view')) this.processChartData(v.full_funnel_chart); } },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.full_funnel_chart != null) this.processChartData(this.dashboardData.full_funnel_chart);
          return;
        }
        if (this.dashboardData && this.dashboardData.full_funnel_chart != null) { this.processChartData(this.dashboardData.full_funnel_chart); return; }
        this.fetchFullFunnelChartData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.full_funnel_chart != null) this.processChartData(this.dashboardData.full_funnel_chart);
            return;
          }
          this.fetchFullFunnelChartData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [];
        }
      },
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    async fetchFullFunnelChartData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.full_funnel_chart != null)) {
        if (this.dashboardData && this.dashboardData.full_funnel_chart != null) this.processChartData(this.dashboardData.full_funnel_chart);
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/full-funnel-chart', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error('Error fetching full funnel chart data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      if (!data || data.length === 0) {
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        this.series = [];
        return;
      }

      const categories = (data.map(item => item && item.stage)).filter(Boolean);
      // For a stacked 100% bar chart representing a funnel, each stage would ideally be a series,
      // but the data structure here is count per stage, which is more suited for a simple bar chart.
      // If the intent is to show progression, a waterfall or funnel specific chart might be better.
      // For now, I will display it as a simple horizontal bar chart showing count per stage.

      this.chartOptions = {
        ...this.chartOptions,
        chart: { ...this.chartOptions.chart, stacked: false, stackType: undefined }, // Change to non-stacked bar
        xaxis: {
          categories: Array.isArray(categories) ? categories : [],
        },
        yaxis: {
          title: {
            text: 'Number of Leads',
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true, // Different colors for each bar
          },
        },
        legend: { show: false }, // No legend needed for single series with distributed colors
      };

      this.series = [{
        name: 'Leads',
        data: data.map(item => (typeof (item && item.count) === 'number' ? item.count : Number(item && item.count) || 0)),
      }];
    },
    handleDataPointSelection(event, chartContext, config) {
      if (config.dataPointIndex !== undefined && this.chartOptions.xaxis.categories[config.dataPointIndex]) {
        const stageName = this.chartOptions.xaxis.categories[config.dataPointIndex];
        this.$emit('chart-point-click', 'CRM Funnel Chart - ' + stageName, '/dashboard/crm/full-funnel-chart/detail/' + stageName, { stage: stageName });
      }
    },
  },
};
</script>

<style scoped>
.full-funnel-chart {
  margin-top: 20px;
  cursor: pointer; /* Added for clickable affordance */
}
</style>